import {
    DashboardOutlined,
    DisconnectOutlined,
    LinkOutlined,
    LoginOutlined,
    ReloadOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Dropdown, MenuProps, Spin } from "antd";
import { ItemType } from "antd/es/menu/interface";

import { SecondaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";

import { SIZES } from "../../constants";
import { StyledActionButton, StyledMoreOutlined } from "../../Styled";

interface MemberActionsProps {
    isProcessing: boolean;
    hasSchedule: boolean;
    canAccess: boolean;
    onLoad: () => void;
    onNavigate: () => void;
    onDashboard: () => void;
    onDelete: () => void;
    onCreateCustomTask: () => void;
}

export const MemberActions = ({
    isProcessing,
    hasSchedule,
    canAccess,
    onLoad,
    onNavigate,
    onDashboard,
    onDelete,
    onCreateCustomTask,
}: MemberActionsProps) => {
    const { role } = useAppSelector((state) => state.user);
    const { offlineMode } = useAppSelector((state) => state.offline);
    const isSVM = role === "svm";

    const stop = (e: React.MouseEvent) => e.stopPropagation();

    const dropdownItems: MenuProps["items"] = [
        {
            key: "dashboard",
            icon: <DashboardOutlined style={{ fontSize: 18 }} />,
            label: <SecondaryText fontSize={14}>Дашборд</SecondaryText>,
            disabled: isSVM || offlineMode,
            visible: true,
        },
        {
            key: "load",
            icon: hasSchedule ? (
                <ReloadOutlined style={{ fontSize: 18 }} />
            ) : (
                <LinkOutlined style={{ fontSize: 18 }} />
            ),
            label: (
                <SecondaryText fontSize={14}>
                    {hasSchedule
                        ? "Перезавантажити маршрут"
                        : "Підключити маршрут"}
                </SecondaryText>
            ),
            disabled: isProcessing || offlineMode,
            visible: true,
        },
        {
            key: "customTask",
            icon: <UserSwitchOutlined style={{ fontSize: 18 }} />,
            label: (
                <SecondaryText fontSize={14}>Створити доручення</SecondaryText>
            ),
            disabled: false,
            visible: true,
        },
        {
            key: "navigate",
            icon: <LoginOutlined style={{ fontSize: 18 }} />,
            label: (
                <SecondaryText fontSize={14}>
                    Відкрити план роботи
                </SecondaryText>
            ),
            disabled: !canAccess,
            visible: canAccess,
        },
        hasSchedule
            ? { type: "divider" as const, key: "divider1", visible: true }
            : undefined,
        {
            key: "delete",
            icon: (
                <DisconnectOutlined
                    style={{ fontSize: 18, color: "#BF2E2D" }}
                />
            ),
            label: (
                <SecondaryText fontSize={14} style={{ color: "#BF2E2D" }}>
                    Відключити маршрут
                </SecondaryText>
            ),
            danger: true,
            disabled: !hasSchedule,
            visible: hasSchedule,
        },
    ].filter((item) => item !== undefined && item.visible);

    const handleMenuClick: MenuProps["onClick"] = ({ key, domEvent }) => {
        domEvent.stopPropagation();

        const clickedItem = dropdownItems.find(
            (i) => i.key === key,
        ) as ItemType & { disabled?: boolean };
        if (!clickedItem || clickedItem.disabled) return;

        switch (key) {
            case "dashboard":
                onDashboard();
                break;
            case "load":
                onLoad();
                break;
            case "navigate":
                onNavigate();
                break;
            case "delete":
                onDelete();
                break;
            case "customTask":
                onCreateCustomTask();
                break;
        }
    };

    if (isProcessing) {
        return (
            <StyledActionButton
                size={SIZES.ACTION_BUTTON}
                icon={<Spin size="small" />}
                disabled
                onClick={stop}
            />
        );
    }

    return (
        <Dropdown
            trigger={["click"]}
            menu={{
                items: dropdownItems,
                onClick: handleMenuClick,
            }}
        >
            <span onClick={stop}>
                <StyledMoreOutlined />
            </span>
        </Dropdown>
    );
};
